import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { CircleCheck, X } from 'lucide-react'
import { toast } from 'sonner'
import { api, ApiError } from '@/lib/api'
import { ENTITY_TYPES, type SubmitCreditApplicationResponse } from '@/lib/api-types'
import type { FieldErrors } from '@/lib/zod-errors'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { DescriptionList } from '@/components/ui/description-list'
import { Field, FieldError, FieldHint, MicroLabel } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { PageHeader } from '@/components/ui/page-header'
import { SectionHeader } from '@/components/ui/section-header'
import { Select } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import {
  MAX_REFERENCES,
  MIN_REFERENCES,
  buildApplicationPayload,
  emptyApplicationForm,
  emptyReferenceRow,
  validateStep,
  type ApplicationFormState,
  type ReferenceFormRow,
} from './application-schema'

/**
 * The credit-application wizard: five single-sitting steps mirroring the
 * paper form (company & legal → contacts & bank → 2–4 trade references →
 * disclosures → review & typed attestation). Validation is the zod mirror of
 * the API schema per step (application-schema.ts), so a form that clears the
 * steps clears the server. On send, the API emails every reference its
 * secure form link automatically.
 */

const STEPS = [
  'Company & legal',
  'Contacts & bank',
  'Trade references',
  'Disclosures',
  'Review & attest',
] as const

const CONTINUE_LABELS = [
  'Continue to contacts',
  'Continue to references',
  'Continue to disclosures',
  'Review the application',
] as const

const SUBMIT_ERROR_COPY: Record<string, string> = {
  validation_failed:
    'Something in the application didn’t validate. Check each step and resend.',
  contact_not_authorized: 'Your Hub access can’t send applications — contact your Green Line rep.',
}

let referenceSeq = 2

export function CreditApplyPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [step, setStep] = useState(0)
  const [form, setForm] = useState<ApplicationFormState>(emptyApplicationForm)
  const [attempted, setAttempted] = useState<Record<number, boolean>>({})
  const [confirmingLeave, setConfirmingLeave] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [created, setCreated] = useState<SubmitCreditApplicationResponse | null>(null)

  // Errors are DERIVED: once a step's advance was attempted, its zod check
  // re-runs on every keystroke so messages clear as fields are fixed.
  const stepErrors: FieldErrors = attempted[step] ? validateStep(step, form) : {}
  const showStepAlert = attempted[step] && Object.keys(stepErrors).length > 0

  const dirty =
    form.company.companyName !== '' ||
    form.company.federalTaxId !== '' ||
    form.apContact.name !== '' ||
    form.bank.bankName !== '' ||
    form.references.some((row) => row.name !== '' || row.email !== '') ||
    form.attestation.signedName !== ''

  // A closed tab loses the whole single-sitting application — warn first.
  useEffect(() => {
    if (!dirty || created) return
    const warn = (event: BeforeUnloadEvent) => event.preventDefault()
    window.addEventListener('beforeunload', warn)
    return () => window.removeEventListener('beforeunload', warn)
  }, [dirty, created])

  const mutation = useMutation({
    mutationFn: () => api.submitCreditApplication(buildApplicationPayload(form)),
    onSuccess: (result) => {
      setCreated(result)
      void queryClient.invalidateQueries({ queryKey: ['credit-application'] })
      window.scrollTo({ top: 0 })
    },
    onError: (error: unknown) => {
      const code = error instanceof ApiError ? error.code : 'request_failed'
      setSubmitError(
        SUBMIT_ERROR_COPY[code] ??
          'That didn’t send. Everything you entered is still here — try again.',
      )
      toast.error('The application didn’t send.')
    },
  })

  function patch<K extends keyof ApplicationFormState>(
    section: K,
    changes: Partial<ApplicationFormState[K]>,
  ) {
    setForm((current) => ({ ...current, [section]: { ...current[section], ...changes } }))
  }

  function updateReference(key: string, changes: Partial<ReferenceFormRow>) {
    setForm((current) => ({
      ...current,
      references: current.references.map((row) => (row.key === key ? { ...row, ...changes } : row)),
    }))
  }

  function addReference() {
    referenceSeq += 1
    setForm((current) =>
      current.references.length >= MAX_REFERENCES
        ? current
        : { ...current, references: [...current.references, emptyReferenceRow(`ref-${referenceSeq}`)] },
    )
  }

  function removeReference(key: string) {
    setForm((current) =>
      current.references.length <= MIN_REFERENCES
        ? current
        : { ...current, references: current.references.filter((row) => row.key !== key) },
    )
  }

  function next() {
    if (Object.keys(validateStep(step, form)).length > 0) {
      setAttempted((current) => ({ ...current, [step]: true }))
      return
    }
    setStep(step + 1)
    window.scrollTo({ top: 0 })
  }

  function send() {
    if (Object.keys(validateStep(4, form)).length > 0) {
      setAttempted((current) => ({ ...current, 4: true }))
      return
    }
    setSubmitError(null)
    mutation.mutate()
  }

  function handleCancel() {
    if (dirty) {
      setConfirmingLeave(true)
      return
    }
    navigate('/credit')
  }

  if (created) {
    return <ApplySuccess result={created} />
  }

  return (
    <div className="mx-auto max-w-form">
      <nav aria-label="Breadcrumb" className="flex items-baseline gap-2 font-mono text-[0.8125rem]">
        <Link
          to="/credit"
          className="rounded-sm text-mute underline-offset-4 transition-colors duration-[180ms] ease-editorial hover:text-brand hover:underline"
        >
          Credit
        </Link>
        <span aria-hidden="true" className="text-muteSoft">
          ·
        </span>
        <span aria-current="page" className="text-inkMid">
          Apply
        </span>
      </nav>

      <PageHeader
        className="mt-3"
        title="Apply for credit"
        description="Tell us about your company, name your trade references, and send it to Green Line’s credit team."
      />

      <Stepper step={step} />

      {step === 0 ? <CompanyStep form={form} errors={stepErrors} onPatch={patch} /> : null}
      {step === 1 ? <ContactsBankStep form={form} errors={stepErrors} onPatch={patch} /> : null}
      {step === 2 ? (
        <ReferencesStep
          references={form.references}
          errors={stepErrors}
          onUpdate={updateReference}
          onAdd={addReference}
          onRemove={removeReference}
        />
      ) : null}
      {step === 3 ? <DisclosuresStep form={form} errors={stepErrors} onPatch={patch} /> : null}
      {step === 4 ? (
        <ReviewStep form={form} errors={stepErrors} onPatch={patch} submitError={submitError} />
      ) : null}

      {showStepAlert ? (
        <p role="alert" className="mt-6 text-sm font-medium text-rust">
          Fix the highlighted fields to keep going.
        </p>
      ) : null}

      {/* Footer controls */}
      <div className="mt-10 flex flex-wrap items-center justify-between gap-3 border-t border-rule/50 pt-6">
        {confirmingLeave ? (
          <div className="flex flex-wrap items-center gap-3">
            <p className="text-sm text-inkMid">
              Leave now and this application is gone — it isn&rsquo;t saved.
            </p>
            <Button type="button" variant="neutral" size="sm" onClick={() => navigate('/credit')}>
              Leave anyway
            </Button>
            <Button type="button" variant="quiet" size="sm" onClick={() => setConfirmingLeave(false)}>
              Keep going
            </Button>
          </div>
        ) : (
          <div className="flex items-center gap-3">
            <Button type="button" variant="quiet" onClick={handleCancel}>
              Cancel
            </Button>
            {step > 0 ? (
              <Button type="button" variant="neutral" onClick={() => setStep(step - 1)}>
                Back
              </Button>
            ) : null}
          </div>
        )}
        {step < 4 ? (
          <Button type="button" size="lg" onClick={next}>
            {CONTINUE_LABELS[step]}
          </Button>
        ) : (
          <Button type="button" size="lg" loading={mutation.isPending} onClick={send}>
            {mutation.isPending ? 'Sending…' : 'Send application'}
          </Button>
        )}
      </div>
    </div>
  )
}

/** The wizard stepper on the manifest rail (the purchasing-wizard treatment). */
function Stepper({ step }: { step: number }) {
  return (
    <nav aria-label="Application steps" className="mt-8">
      <ol className="flex flex-wrap gap-x-6 gap-y-2">
        {STEPS.map((label, index) => {
          const state = index < step ? 'done' : index === step ? 'current' : 'todo'
          return (
            <li
              key={label}
              aria-current={state === 'current' ? 'step' : undefined}
              className={cn(
                'flex items-center gap-2 border-l-2 py-0.5 pl-3',
                state === 'current' ? 'border-brand' : 'border-transparent',
              )}
            >
              {state === 'done' ? (
                <span aria-hidden="true" className="size-1.5 rounded-full bg-brand" />
              ) : (
                <span
                  aria-hidden="true"
                  className={cn(
                    'font-mono text-[0.6875rem]',
                    state === 'current' ? 'text-brandDeep' : 'text-mute',
                  )}
                >
                  {index + 1}
                </span>
              )}
              <span
                className={cn('text-sm', state === 'current' ? 'font-semibold text-ink' : 'text-mute')}
              >
                {label}
              </span>
            </li>
          )
        })}
      </ol>
    </nav>
  )
}

// ---------------------------------------------------------------------------
// Field helpers — MicroLabel + Input/Textarea with error/hint wiring.
// ---------------------------------------------------------------------------

function describedBy(errorId?: string, hintId?: string): string | undefined {
  const ids = [errorId, hintId].filter(Boolean)
  return ids.length ? ids.join(' ') : undefined
}

function TextField({
  id,
  label,
  value,
  onChange,
  error,
  hint,
  type = 'text',
  placeholder,
  autoComplete,
  className,
}: {
  id: string
  label: string
  value: string
  onChange: (value: string) => void
  error?: string
  hint?: string
  type?: string
  placeholder?: string
  autoComplete?: string
  className?: string
}) {
  const hintId = hint ? `${id}-hint` : undefined
  const errorId = error ? `${id}-error` : undefined
  return (
    <Field className={className}>
      <MicroLabel htmlFor={id}>{label}</MicroLabel>
      <Input
        id={id}
        type={type}
        min={type === 'number' ? 0 : undefined}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        autoComplete={autoComplete}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy(errorId, hintId)}
      />
      {error ? <FieldError id={errorId}>{error}</FieldError> : null}
      {hint ? <FieldHint id={hintId}>{hint}</FieldHint> : null}
    </Field>
  )
}

function TextareaField({
  id,
  label,
  value,
  onChange,
  error,
  hint,
  rows = 3,
  placeholder,
  className,
}: {
  id: string
  label: string
  value: string
  onChange: (value: string) => void
  error?: string
  hint?: string
  rows?: number
  placeholder?: string
  className?: string
}) {
  const hintId = hint ? `${id}-hint` : undefined
  const errorId = error ? `${id}-error` : undefined
  return (
    <Field className={className}>
      <MicroLabel htmlFor={id}>{label}</MicroLabel>
      <Textarea
        id={id}
        rows={rows}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy(errorId, hintId)}
      />
      {error ? <FieldError id={errorId}>{error}</FieldError> : null}
      {hint ? <FieldHint id={hintId}>{hint}</FieldHint> : null}
    </Field>
  )
}

// ---------------------------------------------------------------------------
// Step 1 — Company & legal
// ---------------------------------------------------------------------------

function CompanyStep({
  form,
  errors,
  onPatch,
}: {
  form: ApplicationFormState
  errors: FieldErrors
  onPatch: <K extends keyof ApplicationFormState>(
    section: K,
    changes: Partial<ApplicationFormState[K]>,
  ) => void
}) {
  const c = form.company
  const entityTypeError = errors['company.entityType']
  return (
    <div className="mt-8 grid gap-6 sm:grid-cols-2">
      <TextField
        id="company-name"
        label="Legal company name"
        value={c.companyName}
        onChange={(companyName) => onPatch('company', { companyName })}
        error={errors['company.companyName']}
        autoComplete="organization"
      />
      <TextField
        id="company-dba"
        label="DBA / trade name"
        value={c.dba}
        onChange={(dba) => onPatch('company', { dba })}
        error={errors['company.dba']}
        hint="Optional."
      />
      <TextareaField
        id="company-address"
        label="Company address"
        value={c.companyAddress}
        onChange={(companyAddress) => onPatch('company', { companyAddress })}
        error={errors['company.companyAddress']}
        rows={2}
        placeholder="Street, city, state, ZIP"
        className="sm:col-span-2"
      />
      <TextField
        id="company-phone"
        label="Phone"
        value={c.phone}
        onChange={(phone) => onPatch('company', { phone })}
        error={errors['company.phone']}
        type="tel"
        autoComplete="tel"
      />
      <TextField
        id="company-tax-id"
        label="Federal Tax ID or SSN"
        value={c.federalTaxId}
        onChange={(federalTaxId) => onPatch('company', { federalTaxId })}
        error={errors['company.federalTaxId']}
        autoComplete="off"
        hint="Goes straight to the credit team — it never shows in the Hub after you send."
      />
      <Field>
        <MicroLabel htmlFor="company-entity-type">Entity type</MicroLabel>
        <Select
          id="company-entity-type"
          value={c.entityType}
          onChange={(event) =>
            onPatch('company', { entityType: event.target.value as typeof c.entityType })
          }
          aria-invalid={entityTypeError ? true : undefined}
          aria-describedby={entityTypeError ? 'company-entity-type-error' : undefined}
        >
          <option value="">Choose…</option>
          {ENTITY_TYPES.map((type) => (
            <option key={type} value={type}>
              {type}
            </option>
          ))}
        </Select>
        {entityTypeError ? (
          <FieldError id="company-entity-type-error">{entityTypeError}</FieldError>
        ) : null}
      </Field>
      {c.entityType === 'Other' ? (
        <TextField
          id="company-entity-type-other"
          label="Entity type — describe"
          value={c.entityTypeOther}
          onChange={(entityTypeOther) => onPatch('company', { entityTypeOther })}
          error={errors['company.entityTypeOther']}
        />
      ) : null}
      <TextField
        id="company-state-organized"
        label="State organized in"
        value={c.stateOrganized}
        onChange={(stateOrganized) => onPatch('company', { stateOrganized })}
        error={errors['company.stateOrganized']}
        hint="Optional."
      />
      <TextField
        id="company-duns"
        label="DUNS number"
        value={c.duns}
        onChange={(duns) => onPatch('company', { duns })}
        error={errors['company.duns']}
        hint="Optional."
      />
      <TextField
        id="company-type-of-business"
        label="Type of business"
        value={c.typeOfBusiness}
        onChange={(typeOfBusiness) => onPatch('company', { typeOfBusiness })}
        error={errors['company.typeOfBusiness']}
        placeholder="e.g. Cosmetics manufacturer"
        hint="Optional."
      />
      <TextField
        id="company-employees"
        label="Employees"
        value={c.employeeCount}
        onChange={(employeeCount) => onPatch('company', { employeeCount })}
        error={errors['company.employeeCount']}
        type="number"
        hint="Optional."
      />
      <TextField
        id="company-date-established"
        label="Date established"
        value={c.dateEstablished}
        onChange={(dateEstablished) => onPatch('company', { dateEstablished })}
        error={errors['company.dateEstablished']}
        type="date"
        hint="Optional."
      />
      <TextareaField
        id="company-products"
        label="Products you plan to purchase"
        value={c.productsToPurchase}
        onChange={(productsToPurchase) => onPatch('company', { productsToPurchase })}
        error={errors['company.productsToPurchase']}
        rows={2}
        hint="Optional."
        className="sm:col-span-2"
      />
      <TextareaField
        id="company-authorized"
        label="People authorized to purchase"
        value={c.authorizedPersons}
        onChange={(authorizedPersons) => onPatch('company', { authorizedPersons })}
        error={errors['company.authorizedPersons']}
        rows={2}
        hint="Names and titles, one per line. Optional."
        className="sm:col-span-2"
      />
    </div>
  )
}

// ---------------------------------------------------------------------------
// Step 2 — Contacts & bank
// ---------------------------------------------------------------------------

function ContactsBankStep({
  form,
  errors,
  onPatch,
}: {
  form: ApplicationFormState
  errors: FieldErrors
  onPatch: <K extends keyof ApplicationFormState>(
    section: K,
    changes: Partial<ApplicationFormState[K]>,
  ) => void
}) {
  const ap = form.apContact
  const bank = form.bank
  return (
    <div className="mt-8 space-y-10">
      <section aria-labelledby="ap-contact-heading">
        <SectionHeader id="ap-contact-heading" title="Accounts payable contact" />
        <div className="mt-4 grid gap-6 sm:grid-cols-2">
          <TextField
            id="ap-name"
            label="Name"
            value={ap.name}
            onChange={(name) => onPatch('apContact', { name })}
            error={errors['apContact.name']}
            autoComplete="name"
          />
          <TextField
            id="ap-phone"
            label="Phone"
            value={ap.phone}
            onChange={(phone) => onPatch('apContact', { phone })}
            error={errors['apContact.phone']}
            type="tel"
            hint="Optional."
          />
          <TextField
            id="ap-email"
            label="Email"
            value={ap.email}
            onChange={(email) => onPatch('apContact', { email })}
            error={errors['apContact.email']}
            type="email"
            autoComplete="email"
          />
          <TextField
            id="ap-invoice-email"
            label="Invoice email"
            value={ap.invoiceEmail}
            onChange={(invoiceEmail) => onPatch('apContact', { invoiceEmail })}
            error={errors['apContact.invoiceEmail']}
            type="email"
            hint="Where invoices should go, if different. Optional."
          />
        </div>
      </section>

      <section aria-labelledby="bank-heading">
        <SectionHeader id="bank-heading" title="Bank reference" />
        <div className="mt-4 grid gap-6 sm:grid-cols-2">
          <TextField
            id="bank-name"
            label="Bank name"
            value={bank.bankName}
            onChange={(bankName) => onPatch('bank', { bankName })}
            error={errors['bank.bankName']}
          />
          <TextField
            id="bank-account-number"
            label="Account number"
            value={bank.accountNumber}
            onChange={(accountNumber) => onPatch('bank', { accountNumber })}
            error={errors['bank.accountNumber']}
            autoComplete="off"
            hint="Goes straight to the credit team — it never shows in the Hub after you send."
          />
          <TextField
            id="bank-contact"
            label="Bank contact"
            value={bank.contactName}
            onChange={(contactName) => onPatch('bank', { contactName })}
            error={errors['bank.contactName']}
            hint="Optional."
          />
          <TextField
            id="bank-phone"
            label="Bank phone"
            value={bank.phone}
            onChange={(phone) => onPatch('bank', { phone })}
            error={errors['bank.phone']}
            type="tel"
            hint="Optional."
          />
          <TextareaField
            id="bank-address"
            label="Bank address"
            value={bank.address}
            onChange={(address) => onPatch('bank', { address })}
            error={errors['bank.address']}
            rows={2}
            hint="Optional."
            className="sm:col-span-2"
          />
        </div>
      </section>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Step 3 — Trade references (2–4 rows)
// ---------------------------------------------------------------------------

function ReferencesStep({
  references,
  errors,
  onUpdate,
  onAdd,
  onRemove,
}: {
  references: ReferenceFormRow[]
  errors: FieldErrors
  onUpdate: (key: string, changes: Partial<ReferenceFormRow>) => void
  onAdd: () => void
  onRemove: (key: string) => void
}) {
  return (
    <div className="mt-8 space-y-6">
      <p className="text-sm leading-relaxed text-mute">
        Name at least two companies that extend you credit terms. When you send the application,
        each reference gets an email with a secure form link automatically — nothing else for you
        to chase.
      </p>

      <ul className="space-y-4">
        {references.map((row, index) => (
          <li key={row.key}>
            <Card>
              <div className="flex items-center justify-between gap-3">
                <p className="text-sm font-semibold text-ink">Reference {index + 1}</p>
                {references.length > MIN_REFERENCES ? (
                  <button
                    type="button"
                    onClick={() => onRemove(row.key)}
                    aria-label={`Remove reference ${index + 1}`}
                    className="rounded-sm p-1 text-mute transition-colors duration-[180ms] ease-editorial hover:text-rust"
                  >
                    <X className="size-4" />
                  </button>
                ) : null}
              </div>
              <div className="mt-4 grid gap-6 sm:grid-cols-2">
                <TextField
                  id={`${row.key}-name`}
                  label="Company name"
                  value={row.name}
                  onChange={(name) => onUpdate(row.key, { name })}
                  error={errors[`references.${index}.name`]}
                />
                <TextField
                  id={`${row.key}-email`}
                  label="Email"
                  value={row.email}
                  onChange={(email) => onUpdate(row.key, { email })}
                  error={errors[`references.${index}.email`]}
                  type="email"
                  hint="Their reference form link goes to this address."
                />
                <TextField
                  id={`${row.key}-phone`}
                  label="Phone"
                  value={row.phone}
                  onChange={(phone) => onUpdate(row.key, { phone })}
                  error={errors[`references.${index}.phone`]}
                  type="tel"
                  hint="Optional."
                />
                <TextField
                  id={`${row.key}-fax`}
                  label="Fax"
                  value={row.fax}
                  onChange={(fax) => onUpdate(row.key, { fax })}
                  error={errors[`references.${index}.fax`]}
                  hint="Optional."
                />
                <TextareaField
                  id={`${row.key}-address`}
                  label="Address"
                  value={row.address}
                  onChange={(address) => onUpdate(row.key, { address })}
                  error={errors[`references.${index}.address`]}
                  rows={2}
                  hint="Optional."
                  className="sm:col-span-2"
                />
              </div>
            </Card>
          </li>
        ))}
      </ul>

      {references.length < MAX_REFERENCES ? (
        <Button type="button" variant="neutral" onClick={onAdd}>
          Add another reference
        </Button>
      ) : (
        <p className="text-xs text-mute">Four references is the most the application takes.</p>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Step 4 — Disclosures
// ---------------------------------------------------------------------------

function DisclosuresStep({
  form,
  errors,
  onPatch,
}: {
  form: ApplicationFormState
  errors: FieldErrors
  onPatch: <K extends keyof ApplicationFormState>(
    section: K,
    changes: Partial<ApplicationFormState[K]>,
  ) => void
}) {
  const d = form.disclosures
  return (
    <div className="mt-8 space-y-6">
      <p className="text-sm leading-relaxed text-mute">
        Two quick disclosures — leave both unchecked if neither applies.
      </p>

      <Card>
        <Checkbox
          id="disclosure-bankruptcy"
          label="The company or its principals have filed for bankruptcy, or have a filing pending."
          checked={d.bankruptcy}
          onChange={(event) => onPatch('disclosures', { bankruptcy: event.target.checked })}
        />
        {d.bankruptcy ? (
          <TextareaField
            id="disclosure-bankruptcy-details"
            label="Bankruptcy details"
            value={d.bankruptcyDetails}
            onChange={(bankruptcyDetails) => onPatch('disclosures', { bankruptcyDetails })}
            error={errors['disclosures.bankruptcyDetails']}
            rows={3}
            placeholder="When it was filed, the chapter, and where it stands"
            className="mt-4"
          />
        ) : null}
      </Card>

      <Card>
        <Checkbox
          id="disclosure-litigation"
          label="The company is involved in pending litigation."
          checked={d.litigation}
          onChange={(event) => onPatch('disclosures', { litigation: event.target.checked })}
        />
        {d.litigation ? (
          <TextareaField
            id="disclosure-litigation-details"
            label="Litigation details"
            value={d.litigationDetails}
            onChange={(litigationDetails) => onPatch('disclosures', { litigationDetails })}
            error={errors['disclosures.litigationDetails']}
            rows={3}
            placeholder="The nature of the matter and where it stands"
            className="mt-4"
          />
        ) : null}
      </Card>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Step 5 — Review & attest
// ---------------------------------------------------------------------------

/** Sensitive values render masked in the recap — they were typed two steps ago. */
function masked(value: string): string {
  const trimmed = value.trim()
  return trimmed.length > 4 ? `···· ${trimmed.slice(-4)}` : '····'
}

function ReviewStep({
  form,
  errors,
  onPatch,
  submitError,
}: {
  form: ApplicationFormState
  errors: FieldErrors
  onPatch: <K extends keyof ApplicationFormState>(
    section: K,
    changes: Partial<ApplicationFormState[K]>,
  ) => void
  submitError: string | null
}) {
  const a = form.attestation
  const termsError = errors['attestation.termsAccepted']
  const disclosed =
    [form.disclosures.bankruptcy && 'Bankruptcy', form.disclosures.litigation && 'Litigation']
      .filter(Boolean)
      .join(', ') || 'None disclosed'

  return (
    <div className="mt-8 space-y-8">
      <section aria-labelledby="review-company-heading">
        <SectionHeader id="review-company-heading" title="Company & legal" />
        <Card className="mt-3">
          <DescriptionList
            columns={3}
            items={[
              { label: 'Company', value: form.company.companyName },
              { label: 'Entity type', value: form.company.entityType || '—' },
              { label: 'Tax ID', value: masked(form.company.federalTaxId), mono: true },
              { label: 'Phone', value: form.company.phone, mono: true },
              { label: 'Address', value: form.company.companyAddress },
            ]}
          />
        </Card>
      </section>

      <section aria-labelledby="review-contacts-heading">
        <SectionHeader id="review-contacts-heading" title="Contacts & bank" />
        <Card className="mt-3">
          <DescriptionList
            columns={3}
            items={[
              { label: 'AP contact', value: form.apContact.name },
              { label: 'AP email', value: form.apContact.email, mono: true },
              { label: 'Bank', value: form.bank.bankName },
              { label: 'Account number', value: masked(form.bank.accountNumber), mono: true },
            ]}
          />
        </Card>
      </section>

      <section aria-labelledby="review-references-heading">
        <SectionHeader id="review-references-heading" title="Trade references" />
        <Card padding="none" className="mt-3">
          <ul className="divide-y divide-rule/40">
            {form.references.map((row, index) => (
              <li
                key={row.key}
                className="flex flex-wrap items-baseline justify-between gap-2 px-4 py-3"
              >
                <span className="text-sm font-medium text-ink">{row.name || `Reference ${index + 1}`}</span>
                <span className="font-mono text-xs text-mute">{row.email}</span>
              </li>
            ))}
          </ul>
        </Card>
        <p className="mt-2 text-xs text-mute">
          Each reference is emailed a secure form link the moment you send the application.
        </p>
      </section>

      <section aria-labelledby="review-disclosures-heading">
        <SectionHeader id="review-disclosures-heading" title="Disclosures" />
        <Card className="mt-3">
          <p className="text-sm text-ink">{disclosed}</p>
        </Card>
      </section>

      <section aria-labelledby="review-terms-heading">
        <SectionHeader id="review-terms-heading" title="Terms" />
        <Card className="mt-3">
          <p className="text-sm leading-relaxed text-inkMid">
            Accounts approved for credit are Net 30 from the invoice date. Past-due balances may
            carry a service charge, and Green Line may adjust or withdraw terms based on this
            application and your references.
          </p>
        </Card>
      </section>

      <section aria-labelledby="review-signature-heading">
        <SectionHeader id="review-signature-heading" title="Signature" />
        <div className="mt-4 grid gap-6 sm:grid-cols-3">
          <TextField
            id="attest-name"
            label="Your full name"
            value={a.signedName}
            onChange={(signedName) => onPatch('attestation', { signedName })}
            error={errors['attestation.signedName']}
            autoComplete="name"
          />
          <TextField
            id="attest-title"
            label="Your title"
            value={a.signedTitle}
            onChange={(signedTitle) => onPatch('attestation', { signedTitle })}
            error={errors['attestation.signedTitle']}
            autoComplete="organization-title"
          />
          <TextField
            id="attest-date"
            label="Date"
            value={a.signedDate}
            onChange={(signedDate) => onPatch('attestation', { signedDate })}
            error={errors['attestation.signedDate']}
            type="date"
          />
        </div>
        <div className="mt-5">
          <Checkbox
            id="attest-terms"
            label="I confirm this application is true and complete, and I accept Green Line’s credit terms on behalf of the company."
            checked={a.termsAccepted}
            onChange={(event) => onPatch('attestation', { termsAccepted: event.target.checked })}
            aria-invalid={termsError ? true : undefined}
            aria-describedby={termsError ? 'attest-terms-error' : undefined}
          />
          {termsError ? (
            <FieldError id="attest-terms-error">
              Confirm the terms to send your application.
            </FieldError>
          ) : null}
        </div>
      </section>

      {submitError ? (
        <p role="alert" className="rounded-md border border-rust/30 bg-rust/5 px-4 py-3 text-sm text-rust">
          {submitError}
        </p>
      ) : null}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Success terminal
// ---------------------------------------------------------------------------

function ApplySuccess({ result }: { result: SubmitCreditApplicationResponse }) {
  return (
    <div className="mx-auto max-w-2xl py-10 text-center">
      <CircleCheck aria-hidden="true" className="mx-auto size-8 text-brand" />
      <p className="micro-label mt-6">Application sent</p>
      <h1 className="mt-2 font-mono text-3xl font-semibold tracking-tight text-ink">{result.name}</h1>
      <p className="mx-auto mt-4 max-w-md text-[0.9375rem] leading-relaxed text-inkMid">
        It&rsquo;s with Green Line&rsquo;s credit team. Each trade reference you named was emailed a
        secure link to complete their part — no account needed on their end. Watch reference
        progress and the decision on the Credit page; the team reaches out once everything is in.
      </p>
      <div className="mt-8 flex justify-center gap-3">
        <Button asChild>
          <Link to="/credit">View your application</Link>
        </Button>
        <Button asChild variant="quiet">
          <Link to="/account">Back to account</Link>
        </Button>
      </div>
    </div>
  )
}
